import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import config from '../config/api';
import logger from '../logging/logger';

/**
 * Represents a running job that can be cancelled
 */
export interface ActiveJob {
  jobId: string;
  type: 'backtest' | 'ingest' | 'datacheck' | 'other';
  startedAt: Date;
  description?: string;
}

interface JobContextType {
  activeJobs: ActiveJob[];  // Changed from Map to Array for better React reactivity
  registerJob: (jobId: string, type: ActiveJob['type'], description?: string) => void;
  unregisterJob: (jobId: string) => void;
  getActiveJob: (type: ActiveJob['type']) => ActiveJob | undefined;
  hasActiveJob: (type?: ActiveJob['type']) => boolean;
  cancelJob: (jobId: string) => Promise<boolean>;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

interface JobProviderProps {
  children: ReactNode;
}

/**
 * Global context for tracking and managing active jobs (backtests, ingests, etc.)
 * Provides a centralized way to:
 * - Register jobs when they start
 * - Cancel jobs via the Rust backend API
 * - Track active jobs across the application
 */
export const JobProvider: React.FC<JobProviderProps> = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);

  /**
   * Register a new active job
   */
  const registerJob = useCallback((jobId: string, type: ActiveJob['type'], description?: string) => {
    logger.info('JobContext: Registering job', { jobId, type });
    setActiveJobs(prev => {
      // Remove existing job with same ID if it exists
      const filtered = prev.filter(job => job.jobId !== jobId);
      const newJob: ActiveJob = {
        jobId,
        type,
        startedAt: new Date(),
        description,
      };
      const newJobs = [...filtered, newJob];
      logger.info('JobContext: Job registered', { totalActiveJobs: String(newJobs.length) });
      return newJobs;
    });
  }, []);

  /**
   * Unregister a job (when it completes or is cancelled)
   */
  const unregisterJob = useCallback((jobId: string) => {
    logger.info('JobContext: Unregistering job', { jobId });
    setActiveJobs(prev => {
      const filtered = prev.filter(job => job.jobId !== jobId);
      logger.info('JobContext: Job unregistered', { remainingActiveJobs: String(filtered.length) });
      return filtered;
    });
  }, []);

  /**
   * Get the first active job of a specific type
   */
  const getActiveJob = useCallback((type: ActiveJob['type']): ActiveJob | undefined => {
    return activeJobs.find(job => job.type === type);
  }, [activeJobs]);

  /**
   * Check if there's any active job (optionally of a specific type)
   */
  const hasActiveJob = useCallback((type?: ActiveJob['type']): boolean => {
    if (!type) {
      return activeJobs.length > 0;
    }
    return getActiveJob(type) !== undefined;
  }, [activeJobs, getActiveJob]);

  /**
   * Cancel a job via the Rust backend API
   */
  const cancelJob = useCallback(async (jobId: string): Promise<boolean> => {
    logger.info('JobContext: Cancelling job', { jobId });
    
    const cancelEndpoint = `${config.rustApiUrl}/jobs/${jobId}/cancel`;

    try {
      const response = await fetch(cancelEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        logger.info('JobContext: Job cancelled successfully', { jobId });
        unregisterJob(jobId);
        return true;
      } else {
        logger.error('JobContext: Failed to cancel job', { jobId, error: result.error || 'Unknown error' });
        return false;
      }
    } catch (error) {
      logger.error('JobContext: Error cancelling job', { jobId, error: String(error) });
      return false;
    }
  }, [unregisterJob]);

  const contextValue = useMemo<JobContextType>(() => ({
    activeJobs,
    registerJob,
    unregisterJob,
    getActiveJob,
    hasActiveJob,
    cancelJob,
  }), [activeJobs, registerJob, unregisterJob, getActiveJob, hasActiveJob, cancelJob]);

  return (
    <JobContext.Provider value={contextValue}>
      {children}
    </JobContext.Provider>
  );
};

/**
 * Hook to access the Job context
 */
export const useJobs = (): JobContextType => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};

